import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Mail, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Visual side */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 tj-grid-bg opacity-30" />
        <div className="relative">
          <Logo size={56} withText dark />
        </div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-semibold leading-tight max-w-md">
            Helping India&apos;s workforce <span className="text-brand-300">climb higher.</span>
          </h2>
          <p className="mt-4 text-slate-300 max-w-md">
            Tulsi Jobs recruiter console — manage candidates, segregate by job category, and line them up for top employers.
          </p>
        </div>
        <div className="relative text-sm text-slate-400">
          © {new Date().getFullYear()} Tulsi Jobs Placement Consultant
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="lg:hidden mb-8">
            <Logo size={48} />
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-ink-900">
            Sign in
          </h1>
          <p className="text-sm text-ink-600 mt-1">Team member access only.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@tulsijobs.com"
                  required
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-9"
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-brand-700 hover:bg-brand-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="mt-8 text-xs text-ink-600 bg-brand-50 border border-brand-100 rounded-lg p-3">
            <strong className="text-brand-800">Are you a candidate?</strong>{" "}
            Recruiters share a separate public link with you. Ask your recruiter
            for the application form link.
          </div>
        </div>
      </div>
    </div>
  );
}
